import dynamic from "next/dynamic";
import { createPortal } from "react-dom";
import { useRef, useState, useEffect } from "react";
import { useRouter } from "next/router";
import { site } from "config";
import { useSignIn, useSignUp } from "@clerk/nextjs";
import AuthButton from "./AuthButton";

const SignUpSignInButton = dynamic(() => import("./SignUpSignInButton"));
const Consent = dynamic(() => import("components/auth/clerk/Consent"));

import useOutsideClick from "lib/hooks/useOutsideClick";
import { getCookie } from "utils";

import styles from "./SignUpSignInModal.module.scss";

const STEP = {
  FORM: "form",
  VERIFYING: "verifying",
  SECOND_FACTOR: "secondFactor",
  SUCCESS: "success",
  ERROR: "error",
};

const SECOND_FACTOR_STRATEGIES = ["email_code", "phone_code", "totp"];

export default function SignUpSignInModal({
  buttonMode = "icon",
  redirectUrl,
  buttonText,
  buttonClassName,
  onOpen,
}) {
  const [open, setOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [communicationConsent, setCommunicationConsent] = useState(true);
  const [emailAddress, setEmailAddress] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [secondFactorCode, setSecondFactorCode] = useState("");
  const [secondFactorStrategy, setSecondFactorStrategy] = useState(null);
  const [emailFlow, setEmailFlow] = useState(null);
  const [step, setStep] = useState(STEP.FORM);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorDebug, setErrorDebug] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hubspotutk, setHubspotutk] = useState("");
  const [cookieReadAttempts, setCookieReadAttempts] = useState(0);

  const communicationConsentRef = useRef();

  const router = useRouter();
  const baseUrl =
    typeof window !== "undefined" ? window.location.origin : site;
  const defaultRedirectUrl = `${baseUrl}${router.asPath}`;

  const resolveRedirectUrl = (value) => {
    if (!value) {
      return defaultRedirectUrl;
    }

    if (typeof value !== "string") {
      return defaultRedirectUrl;
    }

    if (value.startsWith("http")) {
      try {
        const url = new URL(value);
        if (url.origin !== baseUrl) {
          return `${baseUrl}${url.pathname}${url.search}${url.hash}`;
        }
        return value;
      } catch (error) {
        return defaultRedirectUrl;
      }
    }

    if (value.startsWith("/")) {
      return `${baseUrl}${value}`;
    }

    return `${baseUrl}/${value}`;
  };

  const resolvedRedirectUrl = resolveRedirectUrl(redirectUrl);

  const { isLoaded: isSignInLoaded, signIn, setActive: setActiveSignIn } =
    useSignIn();
  const { isLoaded: isSignUpLoaded, signUp, setActive: setActiveSignUp } =
    useSignUp();

  const handleConsentCheckboxChange = () => {
    setCommunicationConsent((prev) => !prev);
  };

  const handleUserButtonClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsClosing(false);
    setOpen((prev) => !prev);
    if (onOpen) {
      onOpen();
    }
  };

  const handleModalClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setOpen(false);
    }, 300);
  };

  const buttonRef = useRef();
  const portalRef = useRef(null);
  const modalRef = useOutsideClick(handleModalClose, [buttonRef]);

  useEffect(() => {
    portalRef.current = document.querySelector("#portal");

    if (process.env !== "production") {
      return;
    }

    const cookieCheckInterval = setInterval(() => {
      setCookieReadAttempts((prev) => {
        if (prev >= 20) {
          console.log("hubspotutk not found");
          clearInterval(cookieCheckInterval);
          return prev;
        }

        const hubspotutkCookie = getCookie("hubspotutk");

        if (hubspotutkCookie) {
          console.log(hubspotutkCookie);
          setHubspotutk(hubspotutkCookie);
          clearInterval(cookieCheckInterval);
        }

        return prev + 1;
      });
    }, 500);

    return () => clearInterval(cookieCheckInterval); // Cleanup on unmount
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setStep(STEP.FORM);
    setErrorMessage("");
    setErrorDebug("");
    setVerificationCode("");
    setSecondFactorCode("");
    setSecondFactorStrategy(null);
    setEmailFlow(null);
    setIsSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        handleModalClose();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [open]);

  const getFriendlyErrorMessage = (error) => {
    const code = error?.errors?.[0]?.code;

    const errorMap = {
      form_identifier_not_found: "We couldn't find an account with that email.",
      form_identifier_exists: "An account with this email already exists.",
      form_password_required:
        "Passwordless sign-in isn't enabled. Please contact support.",
      form_code_incorrect: "That code is incorrect. Please try again.",
      form_code_expired: "That code expired. Please request a new one.",
    };

    return errorMap[code] || "Something went wrong. Please try again.";
  };

  const reportError = (error) => {
    const code = error?.errors?.[0]?.code;
    const message = error?.errors?.[0]?.message;

    if (process.env.NODE_ENV !== "production") {
      setErrorDebug(code ? `${code}${message ? `: ${message}` : ""}` : "");
    }

    console.error("Clerk auth error", error);
  };

  const handleSuccess = async (createdSessionId, setActive) => {
    await setActive({ session: createdSessionId });
    setStep(STEP.SUCCESS);
    setOpen(false);
    router.push(resolvedRedirectUrl);
  };

  const prepareSecondFactor = async () => {
    const supported =
      signIn?.supportedSecondFactors?.filter((factor) =>
        SECOND_FACTOR_STRATEGIES.includes(factor.strategy)
      ) ?? [];

    const preferred = supported[0];

    if (!preferred) {
      setErrorMessage("Additional verification is required to sign in.");
      setStep(STEP.ERROR);
      return;
    }

    if (["email_code", "phone_code"].includes(preferred.strategy)) {
      await signIn.prepareSecondFactor({ strategy: preferred.strategy });
    }

    setSecondFactorStrategy(preferred.strategy);
    setStep(STEP.SECOND_FACTOR);
  };

  const attemptSignUp = async () => {
    if (!isSignUpLoaded || !signUp) {
      return;
    }

    const signUpAttempt = await signUp.create({
      emailAddress,
      unsafeMetadata: {
        communicationConsent,
        hubspotutk: hubspotutk ?? null,
      },
    });

    if (signUpAttempt.status === "complete") {
      await handleSuccess(signUpAttempt.createdSessionId, setActiveSignUp);
      return;
    }

    await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
    setEmailFlow("signUp");
    setStep(STEP.VERIFYING);
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setErrorDebug("");

    if (!isSignInLoaded || !signIn || !isSignUpLoaded || !signUp) {
      return;
    }

    setIsSubmitting(true);

    try {
      const signInAttempt = await signIn.create({
        identifier: emailAddress,
      });

      if (signInAttempt.status === "complete") {
        await handleSuccess(signInAttempt.createdSessionId, setActiveSignIn);
        return;
      }

      if (signInAttempt.status === "needs_first_factor") {
        const emailFactor = signInAttempt.supportedFirstFactors?.find(
          (factor) => factor.strategy === "email_code"
        );

        if (!emailFactor?.emailAddressId) {
          setErrorMessage(
            "Email code sign in is unavailable for this account."
          );
          setStep(STEP.ERROR);
          return;
        }

        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: emailFactor.emailAddressId,
        });
        setEmailFlow("signIn");
        setStep(STEP.VERIFYING);
        return;
      }

      if (signInAttempt.status === "needs_second_factor") {
        await prepareSecondFactor();
        return;
      }

      setErrorMessage("We couldn't sign you in. Please try again.");
      setStep(STEP.ERROR);
    } catch (error) {
      const code = error?.errors?.[0]?.code;
      if (code === "form_identifier_not_found") {
        try {
          await attemptSignUp();
        } catch (signUpError) {
          reportError(signUpError);
          setErrorMessage(getFriendlyErrorMessage(signUpError));
          setStep(STEP.ERROR);
        }
        return;
      }

      reportError(error);
      setErrorMessage(getFriendlyErrorMessage(error));
      setStep(STEP.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setErrorDebug("");

    if (!isSignUpLoaded || !signUp || !isSignInLoaded || !signIn) {
      return;
    }

    if (!emailFlow) {
      setErrorMessage("Please restart the sign in flow and request a new code.");
      setStep(STEP.ERROR);
      return;
    }

    setIsSubmitting(true);

    try {
      const verification =
        emailFlow === "signIn"
          ? await signIn.attemptFirstFactor({
              strategy: "email_code",
              code: verificationCode,
            })
          : await signUp.attemptEmailAddressVerification({
              code: verificationCode,
            });

      if (verification.status === "complete") {
        await handleSuccess(
          verification.createdSessionId,
          emailFlow === "signIn" ? setActiveSignIn : setActiveSignUp
        );
        return;
      }

      if (verification.status === "needs_second_factor") {
        await prepareSecondFactor();
        return;
      }

      setErrorMessage("We couldn't verify that code. Please try again.");
      setStep(STEP.ERROR);
    } catch (error) {
      const code = error?.errors?.[0]?.code;
      if (!["form_code_incorrect", "form_code_expired"].includes(code)) {
        reportError(error);
        setStep(STEP.ERROR);
      } else {
        setStep(STEP.VERIFYING);
      }
      setErrorMessage(getFriendlyErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSecondFactorSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setErrorDebug("");

    if (!isSignInLoaded || !signIn || !secondFactorStrategy) {
      return;
    }

    setIsSubmitting(true);

    try {
      const secondFactorAttempt = await signIn.attemptSecondFactor({
        strategy: secondFactorStrategy,
        code: secondFactorCode,
      });

      if (secondFactorAttempt.status === "complete") {
        await handleSuccess(
          secondFactorAttempt.createdSessionId,
          setActiveSignIn
        );
        return;
      }

      setErrorMessage("We couldn't verify that code. Please try again.");
      setStep(STEP.ERROR);
    } catch (error) {
      reportError(error);
      setErrorMessage(getFriendlyErrorMessage(error));
      setStep(STEP.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuth = async (strategy) => {
    if (!isSignInLoaded || !signIn) {
      return;
    }

    await signIn.authenticateWithRedirect({
      strategy,
      redirectUrl: "/sso-callback",
      redirectUrlComplete: resolvedRedirectUrl,
    });
  };

  return (
    <>
      <SignUpSignInButton
        onClick={handleUserButtonClick}
        buttonRef={buttonRef}
        mode={buttonMode}
        className={buttonClassName}
      >
        {buttonText}
      </SignUpSignInButton>

      {open &&
        !!portalRef?.current &&
        createPortal(
          <div
            className={`${styles.modalContainer} ${open ? styles.isOpen : ""} ${
              isClosing ? styles.isClosed : ""
            }`}
          >
            <div
              onClose={() => setOpen(false)}
              ref={modalRef}
              className={`${styles.modal}`}
            >
              <button
                className={`${styles.closeButton} close-sign-up-sign-in-modal`}
                onClick={handleModalClose}
                type="button"
              >
                <span className={`${styles.closeIcon}`}>&times;</span>
              </button>
              <section className={`${styles.content}`}>
                {step === STEP.FORM && (
                  <>
                    <div className={`${styles.header}`}>
                      <h2 className={`${styles.title}`}>
                        Sign in or create an account
                      </h2>
                      <p className={`${styles.subtitle}`}>
                        Continue with a social account, or verify your email to
                        get started.
                      </p>
                    </div>
                    <div className={`${styles.oauthButtons}`}>
                      <AuthButton
                        provider="google"
                        onClick={() => handleOAuth("oauth_google")}
                      />
                      <AuthButton
                        provider="facebook"
                        onClick={() => handleOAuth("oauth_facebook")}
                      />
                      <AuthButton
                        provider="apple"
                        onClick={() => handleOAuth("oauth_apple")}
                      />
                    </div>
                    <div className={`${styles.divider}`}>
                      <span>or</span>
                    </div>
                    <form
                      className={`${styles.form}`}
                      onSubmit={handleEmailSubmit}
                    >
                      <div className={`${styles.fieldGroup}`}>
                        <label
                          htmlFor="auth-email"
                          className={`${styles.label}`}
                        >
                          Email
                        </label>
                        <input
                          id="auth-email"
                          type="email"
                          autoComplete="email"
                          className={`${styles.input}`}
                          value={emailAddress}
                          onChange={(event) =>
                            setEmailAddress(event.target.value)
                          }
                          required
                        />
                      </div>
                      {errorMessage && (
                        <p className={`${styles.error}`}>
                          {errorMessage}
                          {errorDebug ? ` (${errorDebug})` : ""}
                        </p>
                      )}
                      <AuthButton
                        provider="email"
                        type="submit"
                        disabled={isSubmitting}
                        className={styles.emailButton}
                      />
                      <Consent
                        handleConsentCheckboxChange={
                          handleConsentCheckboxChange
                        }
                        communicationConsentRef={communicationConsentRef}
                        checked={communicationConsent}
                      />
                    </form>
                  </>
                )}
                {step === STEP.VERIFYING && (
                  <form
                    className={`${styles.form}`}
                    onSubmit={handleVerificationSubmit}
                  >
                    <div className={`${styles.header}`}>
                      <h2 className={`${styles.title}`}>
                        Verify your email
                      </h2>
                      <p className={`${styles.subtitle}`}>
                        Enter the code sent to {emailAddress}.
                      </p>
                    </div>
                    <div className={`${styles.fieldGroup}`}>
                      <label
                        htmlFor="auth-verification-code"
                        className={`${styles.label}`}
                      >
                        Verification code
                      </label>
                      <input
                        id="auth-verification-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className={`${styles.input}`}
                        value={verificationCode}
                        onChange={(event) =>
                          setVerificationCode(event.target.value)
                        }
                        required
                      />
                    </div>
                    {errorMessage && (
                      <p className={`${styles.error}`}>
                        {errorMessage}
                        {errorDebug ? ` (${errorDebug})` : ""}
                      </p>
                    )}
                    <div className={`${styles.actionRow}`}>
                      <button
                        type="button"
                        className={`${styles.secondaryButton}`}
                        onClick={() => setStep(STEP.FORM)}
                        disabled={isSubmitting}
                      >
                        Back
                      </button>
                      <button
                        className={`${styles.primaryButton}`}
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Verifying..." : "Verify"}
                      </button>
                    </div>
                  </form>
                )}
                {step === STEP.SECOND_FACTOR && (
                  <form
                    className={`${styles.form}`}
                    onSubmit={handleSecondFactorSubmit}
                  >
                    <div className={`${styles.header}`}>
                      <h2 className={`${styles.title}`}>
                        Additional verification
                      </h2>
                      <p className={`${styles.subtitle}`}>
                        Enter the code to finish signing in.
                      </p>
                    </div>
                    <div className={`${styles.fieldGroup}`}>
                      <label
                        htmlFor="auth-second-factor-code"
                        className={`${styles.label}`}
                      >
                        Verification code
                      </label>
                      <input
                        id="auth-second-factor-code"
                        type="text"
                        inputMode="numeric"
                        className={`${styles.input}`}
                        value={secondFactorCode}
                        onChange={(event) =>
                          setSecondFactorCode(event.target.value)
                        }
                        required
                      />
                    </div>
                    {errorMessage && (
                      <p className={`${styles.error}`}>
                        {errorMessage}
                        {errorDebug ? ` (${errorDebug})` : ""}
                      </p>
                    )}
                    <div className={`${styles.actionRow}`}>
                      <button
                        type="button"
                        className={`${styles.secondaryButton}`}
                        onClick={() => setStep(STEP.FORM)}
                        disabled={isSubmitting}
                      >
                        Back
                      </button>
                      <button
                        className={`${styles.primaryButton}`}
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Verifying..." : "Verify"}
                      </button>
                    </div>
                  </form>
                )}
                {step === STEP.ERROR && (
                  <div className={`${styles.form}`}>
                    <div className={`${styles.header}`}>
                      <h2 className={`${styles.title}`}>
                        Something went wrong
                      </h2>
                      <p className={`${styles.subtitle}`}>
                        {errorMessage || "Please try again."}
                        {errorDebug ? ` (${errorDebug})` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={`${styles.primaryButton}`}
                      onClick={() => setStep(STEP.FORM)}
                    >
                      Try again
                    </button>
                  </div>
                )}
              </section>
              <div className={`${styles.scrollIndicatorContainer}`}>
                <div className={`${styles.scrollIndicator}`}></div>
              </div>
            </div>
          </div>,
          portalRef.current
        )}
    </>
  );
}
